import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
  IconButton,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Popper,
  MenuItem,
  MenuList,
} from '@mui/material';
import { Search, X, RefreshCw, Plus, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import AddContentModal from '../../components/admin/AddContentModal';
import AddVideoModal from '../../components/admin/AddVideoModal';
import ViewDocumentModal from '../../components/admin/ViewDocumentModal';
import ReplaceDocumentModal from '../../components/admin/ReplaceDocumentModal';
import { VIDEO_PROVIDERS } from '../../constants/videoProviders';

export default function Content() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = parseInt(process.env.REACT_APP_PAGINATION_LIMIT || '10', 10);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [activeVideoProvider, setActiveVideoProvider] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Split button state
  const [splitButtonOpen, setSplitButtonOpen] = useState(false);
  const anchorRef = useRef(null);

  // Initialize react-hook-form for search
  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      searchQuery: '',
    },
    mode: 'onChange',
  });

  const searchQuery = watch('searchQuery');

  // State to track the actual search value being used for API calls
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: (page - 1) * limit,
      };

      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        params.title = appliedSearchQuery.trim();
      }

      const response = await api.admin.getDocuments(params);
      setDocuments(response.documents || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearchQuery]);

  const onSubmit = () => {
    // Apply the search value and reset to page 1
    setAppliedSearchQuery(searchQuery);
    setPage(1);
  };

  const handleClearSearch = () => {
    reset({
      searchQuery: '',
    });
    setAppliedSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchDocuments();
  };

  const handleOpenDeleteDialog = (document) => {
    setDeleteDialog({ open: true, document });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, document: null });
  };

  const handleDelete = async () => {
    const { document } = deleteDialog;
    setActionLoading(document.id);

    try {
      await api.admin.deleteDocument(document.id);
      toast.success('Document deleted successfully');
      handleCloseDeleteDialog();
      fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    } finally {
      setActionLoading(null);
    }
  };

  const handleView = (document) => {
    setSelectedDocument(document);
    setViewModalOpen(true);
  };

  const handleReplace = (document) => {
    setSelectedDocument(document);
    setReplaceModalOpen(true);
  };

  const toTitleCase = (str) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const getDocumentType = (document) => {
    if (document.url && document.url.includes('youtube.com')) {
      return 'YouTube URL';
    }
    if (document.url && document.url.includes('wistia.com')) {
      return 'Wistia Video';
    }
    if (document.file_name) {
      const ext = document.file_name.split('.').pop().toLowerCase();
      const typeMap = {
        txt: 'Text File',
        md: 'Markdown',
        markdown: 'Markdown',
        pdf: 'PDF',
        doc: 'Word Document',
        docx: 'Word Document',
        xls: 'Excel Spreadsheet',
        xlsx: 'Excel Spreadsheet',
        ppt: 'PowerPoint Presentation',
        pptx: 'PowerPoint Presentation',
        png: 'PNG Image',
        jpg: 'JPG Image',
        jpeg: 'JPEG Image',
        tiff: 'TIFF Image',
        bmp: 'BMP Image',
      };
      return typeMap[ext] || ext.toUpperCase();
    }
    return 'Unknown';
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    // Split by underscore and convert to title case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusColor = (status) => {
    const statusMap = {
      published: 'success',
      processing: 'warning',
      failed: 'error',
      qna_pending: 'info',
    };
    return statusMap[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return (
      <div className="flex flex-col">
        <span>{dateStr}</span>
        <span>{timeStr}</span>
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit);

  // Split button handlers
  const handleToggleSplitButton = () => {
    setSplitButtonOpen((prevOpen) => !prevOpen);
  };

  const handleCloseSplitButton = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setSplitButtonOpen(false);
  };

  const handleAddDocument = () => {
    setSplitButtonOpen(false);
    setAddModalOpen(true);
  };

  const handleAddYouTubeVideo = () => {
    setSplitButtonOpen(false);
    setActiveVideoProvider('youtube');
  };

  const handleAddWistiaVideo = () => {
    setSplitButtonOpen(false);
    setActiveVideoProvider('wistia');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Content</h1>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<RefreshCw className="w-4 h-4" />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <ButtonGroup variant="contained" ref={anchorRef} disableElevation>
            <Button
              startIcon={<Plus className="w-4 h-4" />}
              onClick={handleAddDocument}
            >
              Add Document
            </Button>
            <Button
              size="small"
              onClick={handleToggleSplitButton}
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </ButtonGroup>
          <Popper
            open={splitButtonOpen}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleCloseSplitButton}>
                    <MenuList autoFocusItem>
                      <MenuItem onClick={handleAddYouTubeVideo}>
                        Add YouTube Video
                      </MenuItem>
                      <MenuItem onClick={handleAddWistiaVideo}>
                        Add Wistia Video
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>

      {/* Search Bar */}
      <Paper className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="flex gap-2">
          <Controller
            name="searchQuery"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Search documents..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <Search className="w-4 h-4 mr-2 text-gray-400" />,
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={handleClearSearch}>
                      <X className="w-4 h-4" />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
        </form>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="30%"><strong>Document Title</strong></TableCell>
              <TableCell width="15%"><strong>Document Type</strong></TableCell>
              <TableCell width="12%"><strong>Status</strong></TableCell>
              <TableCell width="18%"><strong>Last Updated</strong></TableCell>
              <TableCell width="25%"><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: limit }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No documents found
                </TableCell>
              </TableRow>
            ) : (
              documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>{document.title || 'N/A'}</TableCell>
                  <TableCell>{document.source_type ? toTitleCase(document.source_type) : getDocumentType(document)}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(document.status)}
                      color={getStatusColor(document.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(document.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleView(document)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleReplace(document)}
                      >
                        Update
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(document)}
                        disabled={actionLoading === document.id}
                      >
                        {actionLoading === document.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this document?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Document Modal */}
      <AddContentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchDocuments}
      />

      {/* Add Video Modal (YouTube & Wistia) */}
      <AddVideoModal
        open={activeVideoProvider !== null}
        onClose={() => setActiveVideoProvider(null)}
        onSuccess={fetchDocuments}
        config={VIDEO_PROVIDERS[activeVideoProvider] || VIDEO_PROVIDERS.youtube}
      />

      {/* View Document Modal */}
      <ViewDocumentModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        document={selectedDocument}
      />

      {/* Update Document Modal */}
      <ReplaceDocumentModal
        open={replaceModalOpen}
        onClose={() => setReplaceModalOpen(false)}
        document={selectedDocument}
        onSuccess={fetchDocuments}
      />
    </div>
  );
}

